import { Observable, of, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { CommunicationConfigService, ConfigService } from '@argalis-apps/angular-global';
import { MResa, MUser } from '@argalis-apps/models';
import { AuthService } from '../../../auth/services/auth/auth.service';
import { map } from 'rxjs/operators';
import Fingerprint2 from 'fingerprintjs2';
import { FingerprintService } from '../fingerprint/fingerprint.service';

@Injectable({
  providedIn: 'root'
})
export class CartService {
  private _localCart: MResa.IResa[];
  private _cartChange = new Subject<MResa.IResa[]>();
  private forceWaiting: any;
  public constructorFinished: boolean;
  constructor(
    private http: HttpClient,
    private communicationConfigService: CommunicationConfigService,
    private configService: ConfigService,
    private authService: AuthService,
    private fingerprintService: FingerprintService
  ) {
    // Angular compile d'abord les services, user sera d'abord undefined
    // si le service est appelé plus loin, (user peut être définit) on force le constructor d'attendre la mise à jour du panier
    this.forceWaiting = new Promise<void>(async resolve => {
      const user = this.authService.userConnected;
      if (user && user.login) {
        await this.getUserCart(user);
        resolve(void 0);
      } else {
        resolve(this.readLocalCart());
      }
    });
    this.authService.userChange.subscribe(async usr => {
      await this.getUserCart(usr);
      this.constructorFinished = true;
    });
  }

  readLocalCart(): void {
    if (this.configService.getOptions().useTestData) {
      this._cartChange.next(this._localCart);
      return;
    } else {
      this._localCart = [];
      this._cartChange.next(this._localCart);
      return;
    }
  }

  /**
   * récupère la liste du panier; lance une requête à chaque fois qu'on l'appelle.
   * si on veut simplement consulter le panier, prendre this.resaListObservable()
   * @param {IUser} user
   * @returns {Promise<void>}
   */
  async getUserCart(user?: MUser.IUser): Promise<void> {
    return await new Promise<void>(async resolve => {
      if (((this.configService.getOptions() || ({} as any)).options || ({} as any)).useTestData === true) {
        this._cartChange.next(this._localCart);
        resolve(void 0);
      } else {
        const _fingerprint: string = await new Promise<string>(_resolve => {
          try {
            Fingerprint2.get({}, components => {
              const parameters = components.map(component => component.value);
              const hash = Fingerprint2.x64hash128(parameters.join(''), 31);
              if (hash) _resolve(hash);
            });
          } catch (e) {
            _resolve('');
          }
        });
        let params = new HttpParams();
        if (user) params = params.set('idcontactclient', `${user.userInfo.id}`);
        if (user) params = params.set('idclient', `${user.company.id}`);
        params = params.set('fingerprint', _fingerprint);
        this.http.get<MResa.IResa[]>(`${this.communicationConfigService.getApiUri()}/cart/list`, { params }).subscribe(
          (data: MResa.IResa[]) => {
            this._localCart = data;
            this._cartChange.next(this._localCart);
            resolve(void 0);
          },
          err => {
            resolve(void 0);
          }
        );
      }
    });
  }

  saveAsUserCart(user: MUser.IUser): Promise<void> {
    return new Promise<void>(async resolve => {
      if (this.configService.getOptions().useTestData) {
        for (const resa of this._localCart) {
          resa.id_client = user.company.id;
        }
        resolve(void 0);
      } else {
        await this.fingerprintService.setFingerprint();
        const fingerprint = this.fingerprintService.fingerprint;
        const body = {
          resaList: this._localCart,
          idContactClient: ((user || ({} as any)).userInfo || ({} as any)).id || 0,
          idClient: ((user || ({} as any)).company || ({} as any)).id || 0,
          fingerprint
        };
        this.http
          .post<MResa.IResa[]>(`${this.communicationConfigService.getApiUri()}/cart/multipleNewCart`, body)
          .subscribe(
            (result: any[]) => {
              this._localCart = result.map(el => el.resa);
              this._cartChange.next(this._localCart);
              resolve(void 0);
            },
            () => {
              resolve(void 0);
            }
          );
      }
    });
  }

  resaListObservable(): Observable<MResa.IResa[]> {
    return this._cartChange;
  }

  async getResaList(): Promise<MResa.IResa[]> {
    const user = this.authService.userConnected;
    if (user && user.login) {
      await this.getUserCart(user);
    } else {
      await this.getUserCart();
    }
    return this._localCart;
  }

  // retourne immédiatement les éléments du panier qui possède l'idFormation demandé
  findElementsOfCart(idFormation: number): MResa.IResa[] {
    return this._localCart.filter(el => Number(((el || ({} as any)).formation || ({} as any)).id || 0) === idFormation);
  }

  // retourne en promesse un observable pour se mettre à jour dès un changement
  async findElementsOfCartSubscribe(idFormation: number): Promise<Observable<MResa.IResa[]>> {
    return await new Promise<Observable<MResa.IResa[]>>(resolve => {
      const sendElements = new Subject<MResa.IResa[]>();
      this._cartChange.subscribe(() => {
        const newCart: MResa.IResa[] = this._localCart.filter(
          el => Number(((el || ({} as any)).formation || ({} as any)).id || 0) === idFormation
        );
        // on émet à la fin de la render queue pour que l'observable puisse le récupérer
        setTimeout(() => {
          sendElements.next(newCart);
        });
      });
      resolve(sendElements.asObservable());
    });
  }

  getCartCount(): number {
    return (this._localCart || []).length;
  }

  getCart(): MResa.IResa[] {
    return this._localCart || [];
  }

  async addToCart(resa: MResa.IResa, idModalite = 1): Promise<void> {
    return new Promise<void>(async resolve => {
      if (this.configService.getOptions().useTestData) {
        this._localCart.push(resa);
        this._cartChange.next(this._localCart);
        resolve(void 0);
      } else {
        const user = this.authService.userConnected;
        await this.fingerprintService.setFingerprint();
        const fingerprint = this.fingerprintService.fingerprint;
        const body = {
          resa,
          idContactClient: ((user || ({} as any)).userInfo || ({} as any)).id || 0,
          idClient: ((user || ({} as any)).company || ({} as any)).id || 0,
          fingerprint,
          idModalite: `${idModalite}`
        };
        this.http.post<MResa.IResa[]>(`${this.communicationConfigService.getApiUri()}/cart/newCart`, body).subscribe(
          (result: any) => {
            const _resa: MResa.IResa = result.resa;
            if (!result.isUpdated) {
              this._localCart.push(_resa);
              this._cartChange.next(this._localCart);
              resolve(void 0);
            } else {
              const indexOfCart = (this._localCart as any[]).indexOf(line => Number(line.id || 0) === Number(_resa.id));
              if (indexOfCart !== -1) {
                this._localCart[indexOfCart] = _resa;
                this._cartChange.next(JSON.parse(JSON.stringify(this._localCart)));
                resolve(void 0);
              }
            }
          },
          () => {
            resolve(void 0);
          }
        );
      }
    });
  }

  async removeFromCart(idx: number): Promise<void> {
    return new Promise<void>(async resolve => {
      if (this.configService.getOptions().useTestData) {
        this._localCart.splice(idx, 1);
        this._cartChange.next(this._localCart);
        resolve(void 0);
      } else {
        const user = this.authService.userConnected;
        const idClient = ((user || ({} as any)).company || ({} as any)).id;
        const idContactClient = ((user || ({} as any)).userInfo || ({} as any)).id;
        await this.fingerprintService.setFingerprint();
        const fingerprint = this.fingerprintService.fingerprint;
        let params = new HttpParams();
        params = params.set('resa', `${this._localCart[idx].id}`);
        params = params.set('fingerprint', fingerprint);
        params = params.set('idClient', idClient);
        params = params.set('idContactClient', idContactClient);
        this.http.delete(`${this.communicationConfigService.getApiUri()}/cart/spliceCart/`, { params }).subscribe(
          () => {
            this._localCart.splice(idx, 1);
            this._cartChange.next(this._localCart);
            resolve(void 0);
          },
          () => {
            // error
            resolve(void 0);
          }
        );
      }
    });
  }

  getDetailedResa(id: number): Observable<MResa.IResa> {
    const result = new Subject<MResa.IResa>();
    if (this.configService.getOptions().useTestData) {
      setTimeout(() => result.next(this._localCart.find((resa: MResa.IResa) => resa.id === id) || null));
      return of(this._localCart.find((resa: MResa.IResa) => resa.id === id));
    } else {
      return this.http.get<MResa.IResa>(`${this.communicationConfigService.getApiUri()}/cart/${id}/`).pipe(
        map(
          (data: MResa.IResa) => {
            result.next(data);
            return data;
          },
          error => {
            return result.next(this._localCart.find((resa: MResa.IResa) => resa.id === id) || null);
          }
          // gérer le cas d'erreur ? feedback ?
        )
      );
    }
    //return result;
  }

  validateResa(resa: MResa.IResa, bConfirm = false): Observable<MResa.IResa> {
    const result = new Subject<MResa.IResa>();

    if (this.configService.getOptions().useTestData) {
      setTimeout(() => result.next(resa));
    } else {
      // create prc with statut (bConfirm) ? 'gagné' : 'à recontacter'
      // si bConfirm => supprimer resa
      // sinon associer resa & prc
      return this.http.put<MResa.IResa>(
        `${this.communicationConfigService.getApiUri()}/cart/${resa.selected_session.id}/`,
        {
          resa,
          bConfirm
        }
      );
    }
    return result;
  }

  selectionSession(value) {
    console.log('reussit', value);
  }

  getEffectif(id): Observable<any> {
    return this.http.get<any>(`${this.communicationConfigService.getApiUri()}/cart/session/${id}`);
  }

  createResa(resa: MResa.IResa, bConfirm = false): Observable<MResa.IResa> {
    return this.http.post<MResa.IResa>(`${this.communicationConfigService.getApiUri()}/cart/devis`, { resa, bConfirm });
  }

  generateDoc(resa: MResa.IResa, bConfirm = false): any {
    return this.http.post(`${this.communicationConfigService.getApiUri()}/cart/generateDocument/${resa.id}`, {
      resa,
      bConfirm
    });
  }
}
